<template>
  <v-simple-table class="TagsTable border">
    <thead>
    <tr>
      <th>
        Tag
      </th>
      <th>
        Description
      </th>
    </tr>
    </thead>
    <tbody>
    <tr>
      <th>
        <CodeSnippet :content="'{% if recipient.greeting %}Hello {{ recipient.greeting }},{% else %}Hello,{% endif %}'"
                     @selected="selectTag">
        </CodeSnippet>
      </th>
      <td>
        Use this as a greeting that will work whether the recipient's name is available or not.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet :content="'{% if presentation_name %}{{ share_link }}{% else %}[INSERT LINKS HERE]{% endif %}'"
                     @selected="selectTag">
        </CodeSnippet>
      </th>
      <td>
        Use this tag where you want the sender to insert links.
      </td>
    </tr>
    <template>
      <tr>
        <th>
          <CodeSnippet :content="'{{ custom_message }}'"
                       @selected="selectTag">
          </CodeSnippet>
        </th>
        <td>
          Only use this tag in a template with custom messages enabled.
        </td>
      </tr>
      <tr>
        <th>
          <CodeSnippet :content="'{{ shared_links }}'"
                       @selected="selectTag">
          </CodeSnippet>
        </th>
        <td>
          Only use this tag in template with sharing links enabled.
        </td>
      </tr>
    </template>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ address_list }}'">
        </CodeSnippet>
      </th>
      <td>
        Add this phrase:<br/>
        "Please add <span v-pre>{{ user_from_email }}</span> to your address list to ensure you receive my future emails."
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ address_book }}'">
        </CodeSnippet>
      </th>
      <td>
        Alias for <code>address_list</code>
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.email }}'">
        </CodeSnippet>
      </th>
      <td>
        The recipient's email address.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.encrypted_email }}'">
        </CodeSnippet>
      </th>
      <td>
        An encrypted version of the recipient's email address.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ email }}'">
        </CodeSnippet>
      </th>
      <td>
        The recipient's email address. We recommend using <code v-pre>{{ recipient.email }}</code> instead.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ encrypted_email }}'">
        </CodeSnippet>
      </th>
      <td>
        An encrypted version of the recipient's email address. We recommend using <code v-pre>{{ recipient.encrypted_email }}</code> instead.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.greeting }}'">
        </CodeSnippet>
      </th>
      <td>
        If the recipient is in the sender's
        <router-link :to="{name: `${$root.routePrefix}.user_contacts.index`}" target="_blank">
          contacts list
        </router-link>
        and:
        <ul>
          <li>
            Their greeting type is personal, this will be their first name (Lisa, John, Bob, etc.).
          </li>
          <li>
            Their salutation is available, this wil be their salutation and last name (Dr. Smith, Mrs. Jones, etc.)<br/>
          </li>
          <li>
            As a fallback, it will be their full name
          </li>
        </ul>
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.name }}'">
        </CodeSnippet>
      </th>
      <td>
        If available, the recipient's full name.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.first_name }}'">
        </CodeSnippet>
      </th>
      <td>
        If available, the recipient's first name.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.last_name }}'">
        </CodeSnippet>
      </th>
      <td>
        If available, the recipient's last name.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.salutation }}'">
        </CodeSnippet>
      </th>
      <td>
        If available, the recipient's salutation (Dr., Mr., Mrs., etc.)
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.npi }}'">
        </CodeSnippet>
      </th>
      <td>
        If available, the recipient's National Prescriber ID (NPI)
      </td>
    </tr>
    <tr v-if="team.id === 207">
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ recipient.encrypted_npi }}'">
        </CodeSnippet>
      </th>
      <td>
        If available, the recipient's encrypted National Prescriber ID (NPI)
      </td>
    </tr>
    <template v-if="team.user_contact_address_enabled">
      <tr>
        <th>
          <CodeSnippet @selected="selectTag"
                       :content="'{{ recipient.city }}'">
          </CodeSnippet>
        </th>
        <td>
          If available, the recipient's city
        </td>
      </tr>
      <tr>
        <th>
          <CodeSnippet @selected="selectTag"
                       :content="'{{ recipient.state }}'">
          </CodeSnippet>
        </th>
        <td>
          If available, the recipient's state
        </td>
      </tr>
      <tr>
        <th>
          <CodeSnippet @selected="selectTag"
                       :content="'{{ recipient.postal_code }}'">
          </CodeSnippet>
        </th>
        <td>
          If available, the recipient's ZIP/Postal code
        </td>
      </tr>
    </template>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ jwt }}'">
        </CodeSnippet>
      </th>
      <td>
        An identifier that uniquely identifies the sender, recipient, and template.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ presentation_name }}'">
        </CodeSnippet>
      </th>
      <td>
        The name of the presentation being shared.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ presentation_asset_id }}'">
        </CodeSnippet>
      </th>
      <td>
        The asset ID of the presentation being shared.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ share_url }}'">
        </CodeSnippet>
      </th>
      <td>
        <div class="font-weight-bold">
          This only works in in a Quick-Share Template.
        </div>
        If you want the recipient to click a link to view the file on the Mobile Locker website, insert this tag into your HTML. You'll be notified if/when the recipient clicks the link.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ team_name }}'">
        </CodeSnippet>
      </th>
      <td>
        Your team's name.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ team_unsubscribe_url }}'">
        </CodeSnippet>
      </th>
      <td>
        Your team's Unsubscribe URL.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ template_asset_id }}'">
        </CodeSnippet>
      </th>
      <td>
        The Asset ID of the template.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ template_preheader_text }}'">
        </CodeSnippet>
      </th>
      <td>
        The Preheader text for the template.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_name }}'">
        </CodeSnippet>
      </th>
      <td>
        The name of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_first_name }}'">
        </CodeSnippet>
      </th>
      <td>
        The first name of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_last_name }}'">
        </CodeSnippet>
      </th>
      <td>
        The last name of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_email }}'">
        </CodeSnippet>
      </th>
      <td>
        The email address of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_id }}'">
        </CodeSnippet>
      </th>
      <td>
        The ID of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_salesforce_id }}'">
        </CodeSnippet>
      </th>
      <td>
        The Salesforce ID of the user sending the email. Only relevant for teams using Salesforce, Veeva, or OCE.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_encrypted_email }}'">
        </CodeSnippet>
      </th>
      <td>
        An encrypted version of the email address of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_phone }}'">
        </CodeSnippet>
      </th>
      <td>
        The formatted phone number of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_phone_raw }}'">
        </CodeSnippet>
      </th>
      <td>
        The raw phone number of the user sending the email. It will only print the digits.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_phone_extension }}'">
        </CodeSnippet>
      </th>
      <td>
        The user's phone extension
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_region }}'">
        </CodeSnippet>
      </th>
      <td>
        The user's region
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_district }}'">
        </CodeSnippet>
      </th>
      <td>
        The user's district
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_territory }}'">
        </CodeSnippet>
      </th>
      <td>
        The user's territory
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_photo_url }}'">
        </CodeSnippet>
      </th>
      <td>
        The user's photo URL
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_title }}'">
        </CodeSnippet>
      </th>
      <td>
        The job title of the user sending the email.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_signature }}'">
        </CodeSnippet>
      </th>
      <td>
        The user's email signature. It usually consists of:
        <div v-pre>
          <code>{{ user_name }}</code><br/>
          <code>{{ user_title }}</code> (if set)<br/>
          <code>{{ team_name }}</code><br/>
          <code>{{ user_email }}</code><br/>
          <code>{{ user_phone }}</code> (if set)
        </div>
        Contact Mobile Locker support if you would like to have a different signature format for your users.
      </td>
    </tr>
    <tr>
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ user_time_of_day }}'">
        </CodeSnippet>
      </th>
      <td>
        The current time of day for the user in their time zone.
        <ul>
          <li>
            12:00 am to 11:59 am - "Morning"
          </li>
          <li>
            12:00 pm to 5:59 pm - "Afternoon"
          </li>
          <li>
            6:00 pm to 11:59 pm - "Evening"
          </li>
        </ul>
        <p>
          This can be used to write "Good [Morning|Afternoon|Evening]"
        </p>
      </td>
    </tr>
    <tr v-if="canTeamUseSinch">
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="'{{ sms.registration_url }}'">
        </CodeSnippet>
      </th>
      <td>
        The SMS opt-in registration URL for your team. Customers must consent to receive text messages before you can send them SMS communications.
      </td>
    </tr>
    <tr v-if="customTags.length > 0">
      <th colspan="2"
          class="text-h6 px-2">
        Custom Tags for this Template
      </th>
    </tr>
    <tr v-for="customTag in customTags"
        :key="customTag.id">
      <th>
        <CodeSnippet @selected="selectTag"
                     :content="`{{ ${customTag.tag_name} }}`">
        </CodeSnippet>
      </th>
      <td>
        <div>
          Type: {{ customTag.tag_type }}
        </div>
        <div v-if="customTag.instructions">
          Instructions: {{ customTag.instructions }}
        </div>
        <div v-if="customTag.options.length > 0">
          Options:
          <ul>
            <li v-for="option in customTag.options"
                :key="option.value">
              <template v-if="customTag.is_html">
                {{ option.name }}
              </template>
              <template v-else>
                {{ option.value }}
              </template>
            </li>
          </ul>
        </div>
      </td>
    </tr>
    </tbody>
  </v-simple-table>
</template>
<script>
import {mapGetters} from 'vuex';
import CodeSnippet from './CodeSnippet';
import shareTemplateService from '../../../../../ml/services/shareTemplateService';
import {isInteger, isObject} from 'lodash';

export default {
  components: {CodeSnippet},
  props: {
    template: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      customTags: [],
    }
  },
  computed: {
    ...mapGetters([
      'isCampaignsEnabled', // MLD-3998
      'canTeamUseSinch', // MLD-7744
      'team',
      'user',
    ]),
    templateID() {
      return this.template?.id;
    }
  },
  created() {
    //this.fetchData();
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      if (isObject(this.template) && isInteger(this.templateID)) {
        this.customTags = await shareTemplateService.getCustomTagsForTemplate(this.templateID)
      } else {
        this.customTags = [];
      }
    },
    selectTag(content) {
      this.$emit('selected', content);
    }
  }
}
</script>
